/**
 * Echobot Wrapper
 * Combines Echobot robot and chat functionality
 */

import React, { useEffect, useState } from 'react';
import Echobot from './Echobot';
import EchobotChat from './EchobotChat';
import { useEchobot } from '../../contexts/EchobotContext';
import { EchobotContext, EchobotContextData, UserDataContext } from '../../types/echobot';
import { echobotService } from '../../services/echobotService';

interface EchobotWrapperProps {
  context: EchobotContext;
  userRole: string;
  organizationStats: {
    leadCount: number;
    opportunityCount: number;
    campaignCount: number;
    taskCount?: number;
    overdueTasks?: number;
    inactiveOpportunities?: number;
    unassignedLeads?: number;
  };
  // User-specific data for AI context
  userData?: UserDataContext;
  onActionClick?: (action: string) => void;
  showOnMount?: boolean;
  enableProactive?: boolean;
}

const EchobotWrapper: React.FC<EchobotWrapperProps> = ({
  context,
  userRole,
  organizationStats,
  userData,
  onActionClick,
  showOnMount = true,
  enableProactive = true
}) => {
  const echobotContext = useEchobot();
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Update context when props change
  useEffect(() => {
    echobotContext.setContext(context);
  }, [context, echobotContext]);

  // Show welcome message on first visit or time-based greeting
  useEffect(() => {
    if (!showOnMount) return;

    const now = new Date();
    const hour = now.getHours();
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;

    // Time-based greetings (only on dashboard, once per session)
    if (context === 'dashboard') {
      // Good morning (6-12)
      if (hour >= 6 && hour < 12 && !sessionStorage.getItem('greeting-shown-today')) {
        echobotContext.showMessage('Guten Morgen! Bereit für einen produktiven Tag? ☀️', 'wave', 5000);
        sessionStorage.setItem('greeting-shown-today', 'true');
        return;
      }
      
      // End of day (17-20)
      if (hour >= 17 && hour < 20 && !sessionStorage.getItem('eod-shown-today')) {
        const openTasks = organizationStats.taskCount || 0;
        if (openTasks > 0) {
          echobotContext.showMessage(
            `Fast geschafft! Noch ${openTasks} offene Task${openTasks > 1 ? 's' : ''}! 🌙`,
            'question',
            6000,
            { label: 'Tasks prüfen', action: 'navigate-to-tasks' }
          );
          sessionStorage.setItem('eod-shown-today', 'true');
          return;
        }
      }

      // Weekend greeting (Saturday/Sunday)
      if (isWeekend && !sessionStorage.getItem('weekend-shown')) {
        echobotContext.showMessage('Schönes Wochenende! 🎉 Montag geht\'s weiter!', 'celebrate', 5000);
        sessionStorage.setItem('weekend-shown', 'true');
        return;
      }
    }

    // Regular welcome message on first visit
    if (echobotService.isFirstVisit(context)) {
      const welcomeMsg = echobotService.getWelcomeMessage(context);
      echobotContext.showMessage(welcomeMsg.content, welcomeMsg.animation, 4000);
    }
  }, [context, showOnMount, echobotContext, organizationStats.taskCount]);

  // Check for proactive events and task warnings
  useEffect(() => {
    if (!enableProactive) return;

    const checkProactiveEvents = async () => {
      const contextData: EchobotContextData = {
        page: context,
        userRole,
        organizationStats: {
          leadCount: organizationStats.leadCount,
          opportunityCount: organizationStats.opportunityCount,
          campaignCount: organizationStats.campaignCount,
          taskCount: organizationStats.taskCount
        }
      };

      const event = await echobotService.detectProactiveEvent(contextData);
      if (event) {
        echobotContext.showMessage(event.message, event.animation, 6000, event.actionButton);
        return;
      }

      // Additional proactive checks with context awareness
      if (context === 'dashboard' || context === 'tasks') {
        // Check for task warnings (will be passed via props)
        const taskCount = organizationStats.taskCount || 0;
        
        if (taskCount > 0 && !echobotService.isTipShown('task-reminder-' + new Date().toDateString())) {
          // Show daily task reminder
          echobotContext.showMessage(
            `Du hast ${taskCount} offene Task${taskCount > 1 ? 's' : ''}! Zeit sie anzugehen! 💪`,
            'question',
            6000,
            {
              label: 'Tasks ansehen',
              action: 'navigate-to-tasks'
            }
          );
          echobotService.markTipShown('task-reminder-' + new Date().toDateString());
        }
      }
    };

    // Check after 5 seconds
    const timer = setTimeout(checkProactiveEvents, 5000);
    return () => clearTimeout(timer);
    // Depend on the primitive stats the check reads — not the `organizationStats`
    // object, which is a fresh literal on every parent render and would otherwise
    // clear + re-arm this 5s timer on each render (timer churn, unreliable firing).
  }, [context, userRole, organizationStats.leadCount, organizationStats.opportunityCount, organizationStats.campaignCount, organizationStats.taskCount, enableProactive, echobotContext]);

  // Handle robot click - open chat
  const handleRobotClick = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      echobotContext.openChat();
    } else {
      echobotContext.closeChat();
    }
  };

  // Handle action button clicks
  const handleActionClick = (action: string) => {
    if (action === 'robot-clicked') {
      handleRobotClick();
      return;
    }

    if (onActionClick) {
      onActionClick(action);
    }

    // Default action handlers
    switch (action) {
      case 'navigate-to-google-ads':
        window.location.hash = '#google-ads';
        break;
      case 'navigate-to-dashboard':
        window.location.hash = '#dashboard';
        break;
      case 'navigate-to-tasks':
        window.location.hash = '#tasks';
        break;
      case 'navigate-to-opportunities':
        window.location.hash = '#opportunities';
        break;
      case 'navigate-to-leads':
        window.location.hash = '#leads';
        break;
      case 'navigate-to-companies':
        window.location.hash = '#companies';
        break;
      // Add more default actions as needed
    }

    // Close chat if action was clicked
    setIsChatOpen(false);
    echobotContext.closeChat();
  };

  // Build context data for chat
  const contextData: EchobotContextData = {
    page: context,
    userRole,
    organizationStats: {
      leadCount: organizationStats.leadCount,
      opportunityCount: organizationStats.opportunityCount,
      campaignCount: organizationStats.campaignCount,
      taskCount: organizationStats.taskCount,
      overdueTasks: organizationStats.overdueTasks,
      inactiveOpportunities: organizationStats.inactiveOpportunities,
      unassignedLeads: organizationStats.unassignedLeads
    },
    userData: userData
  };

  if (!echobotContext.isVisible) {
    return null;
  }

  return (
    <>
      {/* Echobot Robot */}
      <div className="fixed bottom-14 right-6 z-40">
        <Echobot
          initialMessage={echobotContext.currentMessage?.content}
          initialAnimation={echobotContext.currentMessage?.animation}
          context={context}
          onActionClick={handleActionClick}
          showOnMount={showOnMount}
          enableMouseTracking={true}
        />
      </div>

      {/* Chat Dialog */}
      {isChatOpen && (
        <EchobotChat
          onClose={() => {
            setIsChatOpen(false);
            echobotContext.closeChat();
          }}
          currentContext={contextData}
          onActionClick={handleActionClick}
        />
      )}
    </>
  );
};

export default EchobotWrapper;
